import React, { useState } from 'react';
import { Field, Input, Textarea, Button, Badge, Row, SaveBar, uid, move } from './ui';
import useDraft from './useDraft';

export default function PlayersEditor({ stored, usedStatKeys }) {
  const { draft, setDraft, dirty, saving, error, save, reset } = useDraft('players', stored);
  const [openId, setOpenId] = useState(null);

  const patch = (id, next) => setDraft((d) => d.map((p) => (p.id === id ? next : p)));
  const remove = (id) => {
    if (!window.confirm('Maknuti igrača s popisa? Njegovi rezultati ostaju, ali nestaje s kartica.')) return;
    setDraft((d) => d.filter((p) => p.id !== id));
  };

  const add = () => {
    const p = { id: uid('p'), name: '', role: '', desc: '', image: '/img/', statKey: '' };
    setDraft((d) => [...d, p]);
    setOpenId(p.id);
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Button variant="dark" onClick={add}>+ Novi igrač</Button>
        <span className="text-xs text-slate-500">
          Redoslijed ovdje = redoslijed na stranici. Slike dodaj u <code className="rounded bg-slate-100 px-1">public/img/</code> i upiši putanju.
        </span>
      </div>

      <div className="space-y-2">
        {draft.map((p, idx) => {
          const keyMissing = p.statKey && !usedStatKeys.has(p.statKey);
          return (
            <Row
              key={p.id}
              open={openId === p.id}
              onToggle={() => setOpenId(openId === p.id ? null : p.id)}
              summary={p.name || '(bez imena)'}
              meta={
                <>
                  {p.role && <Badge>{p.role}</Badge>}
                  {p.statKey
                    ? <Badge tone={keyMissing ? 'amber' : 'blue'}>{p.statKey}</Badge>
                    : <Badge tone="red">nema nadimka za statistiku</Badge>}
                  {keyMissing && <Badge tone="amber">nema mečeva</Badge>}
                </>
              }
              onDelete={() => remove(p.id)}
              onUp={idx > 0 ? () => setDraft((d) => move(d, idx, idx - 1)) : undefined}
              onDown={idx < draft.length - 1 ? () => setDraft((d) => move(d, idx, idx + 1)) : undefined}
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Puno ime" hint='Npr. Tudor Valter "Bimbo"'>
                  <Input value={p.name} onChange={(e) => patch(p.id, { ...p, name: e.target.value })} />
                </Field>
                <Field label="Funkcija" hint="Npr. GLAVNI UREDNIK">
                  <Input value={p.role} onChange={(e) => patch(p.id, { ...p, role: e.target.value })} />
                </Field>
                <Field
                  label="Nadimak za statistiku"
                  hint="Mora se točno poklapati s imenom koje biraš kod rezultata (npr. Bimbo)."
                >
                  <Input value={p.statKey} onChange={(e) => patch(p.id, { ...p, statKey: e.target.value })} />
                </Field>
                <Field label="Slika" hint="Putanja u public/ (npr. /img/tudor.jpg) ili puni URL.">
                  <Input value={p.image} onChange={(e) => patch(p.id, { ...p, image: e.target.value })} />
                </Field>
                <Field label="Opis" className="sm:col-span-2">
                  <Textarea value={p.desc} onChange={(e) => patch(p.id, { ...p, desc: e.target.value })} />
                </Field>
                {p.image && (
                  <div className="sm:col-span-2">
                    <img
                      src={p.image}
                      alt=""
                      className="h-28 w-24 border border-slate-300 object-cover"
                      onError={(e) => { e.target.style.opacity = 0.2; }}
                    />
                  </div>
                )}
              </div>
            </Row>
          );
        })}
      </div>

      <SaveBar dirty={dirty} saving={saving} error={error} onSave={save} onReset={reset} count={draft.length} />
    </div>
  );
}
